import { ButtonHTMLAttributes, ReactNode, forwardRef } from "react";
import { cn } from "@/lib/utils";
import { HoneycombSpinner } from "./HoneycombSpinner";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "success" | "outline";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      children,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      "inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

    const variantClasses = {
      primary:
        "bg-honey hover:bg-honey-dark text-bee-black shadow-md hover:shadow-lg active:scale-[0.98]",
      secondary:
        "bg-honey-dark hover:bg-bee-russet text-white shadow-md hover:shadow-lg active:scale-[0.98]",
      ghost:
        "hover:bg-muted text-foreground",
      danger:
        "bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-md hover:shadow-lg active:scale-[0.98]",
      success:
        "bg-success hover:bg-success/90 text-success-foreground shadow-md hover:shadow-lg active:scale-[0.98]",
      outline:
        "border-2 border-honey hover:bg-honey/10 text-foreground",
    };

    const sizeClasses = {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          isLoading && "cursor-wait",
          className
        )}
        {...props}
      >
        {isLoading && <HoneycombSpinner size={16} />}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

